/**
 * Iter 122 · Página de resultado del pago (Redsys redirect OK/KO)
 * Rutas: /tienda/pegatinas/gracias · /tienda/pegatinas/error
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, XCircle, Package, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API, withCredentials: false });

export default function TiendaPegatinasResultado({ ok }) {
  const loc = useLocation();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = new URLSearchParams(loc.search);
  const orderQP = params.get("order");
  const order = orderQP || (typeof localStorage !== "undefined" ? localStorage.getItem("dnf_last_tienda_order") : null);

  useEffect(() => {
    if (!order) { setLoading(false); return; }
    // Reintentamos un par de veces: el webhook puede tardar 1-3s en llegar
    let attempts = 0;
    let cancelled = false;
    const load = async () => {
      while (!cancelled && attempts < 5) {
        try {
          const r = await publicApi.get(`/public/tienda/pedidos/${order}`);
          setPedido(r.data);
          if (r.data.estado !== "pendiente_pago") break;
        } catch { /* noop */ }
        attempts += 1;
        await new Promise((res) => setTimeout(res, 1500));
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [order]);

  const isPagado = pedido?.estado === "pagado" || pedido?.estado === "enviado";
  const success = ok && isPagado;

  return (
    <div className="tienda-scope min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900 flex items-center justify-center px-4">
      <Card className="max-w-md w-full border-slate-200 shadow-lg">
        <CardContent className="p-6 text-center">
          {loading ? (
            <>
              <Loader2 className="w-12 h-12 text-slate-400 animate-spin mx-auto mb-3" />
              <p className="text-slate-600 text-sm">Verificando el pago…</p>
            </>
          ) : success ? (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
              <h1 className="text-2xl font-bold" data-testid="tienda-resultado-ok">¡Pago confirmado!</h1>
              <p className="text-slate-600 text-sm mt-2">
                Tu pedido <span className="font-mono">{order}</span> se ha registrado correctamente.
              </p>
              {pedido && (
                <div className="bg-slate-50 border border-slate-200 rounded p-3 mt-4 text-left text-sm">
                  <div><span className="text-slate-500">Material:</span> {pedido.material_nombre}</div>
                  <div><span className="text-slate-500">Cantidad:</span> {pedido.cantidad_total} uds</div>
                  <div><span className="text-slate-500">Importe:</span> <span className="font-semibold">{Number(pedido.importe_total).toFixed(2)}€</span></div>
                  <div><span className="text-slate-500">Email:</span> {pedido.email}</div>
                </div>
              )}
              <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1">
                <Package className="w-3.5 h-3.5" /> Te enviaremos un email con el seguimiento.
              </p>
            </>
          ) : (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
              <h1 className="text-2xl font-bold" data-testid="tienda-resultado-ko">Pago no completado</h1>
              <p className="text-slate-600 text-sm mt-2">
                {pedido?.estado === "pendiente_pago"
                  ? "Tu pago está aún procesándose. Recibirás un email cuando se confirme."
                  : "No pudimos procesar tu pago. Puedes intentarlo de nuevo cuando quieras."}
              </p>
              {order && (
                <p className="text-xs text-slate-500 mt-3">
                  Referencia: <span className="font-mono">{order}</span>
                </p>
              )}
            </>
          )}
          <Button
            asChild
            className="mt-6 bg-fuchsia-600 hover:bg-fuchsia-700 text-white w-full"
            data-testid="tienda-resultado-volver">
            <a href="/tienda/pegatinas">Volver a la tienda</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
